import { Capacitor } from "@capacitor/core";
import { getNativeLivoraCallStatus } from "../plugins/NativeLivoraCall";

/**
 * Call platform gate — WebView / Web Zego default.
 * Plugin presence is not native call readiness. Billing remains server-only.
 *
 * REACT_APP_LIVORA_CALL_PLATFORM=web|native|auto
 */

const VALID_CALL_PLATFORMS = new Set(["web", "native", "auto"]);

function normalizeLivoraCallPlatform() {
  const raw = String(process.env.REACT_APP_LIVORA_CALL_PLATFORM || "")
    .trim()
    .toLowerCase();
  if (!raw || !VALID_CALL_PLATFORMS.has(raw)) {
    return "web";
  }
  return raw;
}

function isNativeCallPluginPresent() {
  try {
    if (typeof window === "undefined") return false;
    return !!(window.Capacitor?.Plugins?.NativeLivoraCall);
  } catch {
    return false;
  }
}

function isNativeCallReady() {
  // Future: async getStatus() from NativeLivoraCall plugin.
  return false;
}

export function getCallPlatformMode() {
  const livoraCallPlatform = normalizeLivoraCallPlatform();
  const envRaw = String(process.env.REACT_APP_LIVORA_CALL_PLATFORM || "").trim();
  const nativePluginPresent = isNativeCallPluginPresent();
  const nativeReady = isNativeCallReady();
  const nativeAvailable = nativePluginPresent && nativeReady;

  const hasCapacitor = typeof window !== "undefined" && !!window.Capacitor;
  let isNativePlatform = false;
  let platform = "web";

  try {
    isNativePlatform = hasCapacitor && Capacitor.isNativePlatform() === true;
    platform = hasCapacitor ? String(Capacitor.getPlatform() || "unknown") : "web";
  } catch {
    isNativePlatform = false;
    platform = "web";
  }

  const runtimeNativeAndroid = isNativePlatform && platform === "android";

  let useNativeCall = false;
  let useWebCall = true;
  let reason = "forced_web";
  let nativeCallsEnabled = false;
  let nativeFlagEnabled = false;

  if (livoraCallPlatform === "web") {
    reason = "forced_web";
  } else if (livoraCallPlatform === "native") {
    nativeFlagEnabled = true;
    useNativeCall = false;
    useWebCall = true;
    nativeCallsEnabled = false;
    reason = nativePluginPresent
      ? "native_requested_but_not_ready"
      : "native_requested_but_plugin_missing";
  } else if (livoraCallPlatform === "auto") {
    reason = "auto_web_fallback";
    useNativeCall = false;
    useWebCall = true;
    nativeCallsEnabled = false;
  }

  return {
    livoraCallPlatform,
    nativePluginPresent,
    nativeReady,
    nativeAvailable,
    isCapacitorNative: isNativePlatform,
    platform,
    nativeFlagEnabled,
    runtimeNativeAndroid,
    nativeCallsEnabled,
    useNativeCall,
    useWebCall,
    reason,
    hasCapacitor,
    isNativePlatform,
    envNativeFlag: envRaw,
  };
}

export function logCallPlatformMode() {
  const mode = getCallPlatformMode();
  console.log(`[callPlatform] LIVORA_CALL_PLATFORM=${mode.livoraCallPlatform}`);
  console.log(`[callPlatform] nativePluginPresent=${mode.nativePluginPresent}`);
  console.log(`[callPlatform] nativeReady=${mode.nativeReady}`);
  console.log(`[callPlatform] nativeAvailable=${mode.nativeAvailable}`);
  console.log(`[callPlatform] envNativeFlag=${mode.envNativeFlag}`);
  console.log(`[callPlatform] runtimeNativeAndroid=${mode.runtimeNativeAndroid}`);
  console.log(`[callPlatform] nativeCallsEnabled=${mode.nativeCallsEnabled}`);
  console.log(`[callPlatform] hasCapacitor=${mode.hasCapacitor}`);
  console.log(`[callPlatform] isNativePlatform=${mode.isNativePlatform}`);
  console.log(`[callPlatform] platform=${mode.platform}`);
  console.log(`[callPlatform] useNativeCall=${mode.useNativeCall}`);
  console.log(`[callPlatform] useWebCall=${mode.useWebCall}`);
  console.log(`[callPlatform] reason=${mode.reason}`);
  return mode;
}

/**
 * Async read of real NativeLivoraCall plugin status (Android stub).
 * Logging/diagnostics only — does not affect getCallPlatformMode() routing.
 */
export async function getNativeCallStatusForLogging() {
  return getNativeLivoraCallStatus();
}

/**
 * Fire-and-forget log of real NativeLivoraCall plugin status from Android.
 */
export async function logNativeCallStatusForLogging() {
  const status = await getNativeCallStatusForLogging();
  console.log(`[callPlatform] nativeLivoraCallPresent=${status.present}`);
  console.log(`[callPlatform] nativeLivoraCallReady=${status.ready}`);
  console.log(`[callPlatform] nativeLivoraCallAvailable=${status.available}`);
  console.log(`[callPlatform] nativeLivoraCallReason=${status.reason}`);
  if (status.plugin) {
    console.log(`[callPlatform] nativeLivoraCallPlugin=${status.plugin}`);
  }
  if (status.message) {
    console.log(`[callPlatform] nativeLivoraCallMessage=${status.message}`);
  }
  if (status.error) {
    console.log(`[callPlatform] nativeLivoraCallError=${status.error}`);
  }
  return status;
}
